<template>
    <admin-table-row :item="literature">
        <td class="column-cover">
            <div class="literature-table-thumb flex items-center justify-center">
                <img
                    v-if="literature.image_path"
                    :src="imageUrl(literature.image_path)"
                    :alt="(literature.name || 'Literature') + ' cover'"
                    class="max-w-full max-h-full"
                />
                <span v-else class="text-pip-gray" aria-label="No cover image">—</span>
            </div>
        </td>
        <td class="column-title">
            <router-link
                class="literature-title block font-semibold"
                :to="{ name: 'literature-edit', params: { id: literature.id } }"
            >
                {{ literature.name || "Untitled literature" }}
            </router-link>
            <div class="row-actions mt-1 text-xs">
                <router-link :to="{ name: 'literature-edit', params: { id: literature.id } }">Edit</router-link>
                <template v-if="literature.path">
                    <span aria-hidden="true"> | </span>
                    <a :href="pdfUrl(literature.path)" target="_blank" rel="noopener">View PDF</a>
                </template>
                <span aria-hidden="true"> | </span>
                <button
                    type="button"
                    class="delete-row-action"
                    :disabled="deleting"
                    @click="$emit('delete', literature)"
                >
                    {{ deleting ? "Deleting…" : "Delete" }}
                </button>
            </div>
            <span v-if="literature.path" class="pdf-name block mt-1 text-xs text-pip-gray">
                {{ literature.path }}
            </span>
        </td>
        <td>{{ literature.catagory || "—" }}</td>
        <td>
            <span class="status-badge" :class="statusClass(literature.status)">
                {{ literature.status || "Not set" }}
            </span>
        </td>
        <td class="text-nowrap">{{ formatDate(literature.start_date) }}</td>
        <td>
            <div class="flex flex-wrap gap-1">
                <span :class="['destination-state', { 'is-active': isEnabled(literature.web_retail) }]">Retail</span>
                <span :class="['destination-state', { 'is-active': isEnabled(literature.web_boss) }]">Boss</span>
                <span :class="['destination-state', { 'is-active': isEnabled(literature.web_cat) }]">Catalog</span>
            </div>
        </td>
    </admin-table-row>
</template>

<script>
import { AdminTableRow } from "@c/shared/admin";

export default {
    name: "LiteratureAdminTableRow",
    components: { AdminTableRow },
    props: {
        literature: {
            type: Object,
            required: true,
        },
        deleting: { type: Boolean, default: false },
    },
    methods: {
        isEnabled(value) {
            return value === 1 || value === "1" || value === "y" || value === true;
        },
        assetUrl(path, legacyDirectory) {
            if (!path) return "";
            if (/^(https?:)?\/\//.test(path) || path.charAt(0) === "/") return path;
            if (path.indexOf("media/") === 0) return `/${path}`;
            return `${legacyDirectory}${path}`;
        },
        imageUrl(path) {
            return this.assetUrl(path, "/images/catalogs/");
        },
        pdfUrl(path) {
            return this.assetUrl(path, "/docs/Literature/");
        },
        formatDate(value) {
            if (!value) return "—";
            const date = new Date(value);
            return isNaN(date) ? value : date.toLocaleDateString();
        },
        statusClass(status) {
            const classes = {
                published: "status-badge--published",
                draft: "status-badge--draft",
                archived: "status-badge--archived",
            };
            return classes[(status || "").toLowerCase()] || "status-badge--unset";
        },
    },
};
</script>

<style scoped lang="scss">
td {
    padding: 0.55rem 0.65rem;
    border-top: 1px solid var(--color-gallery);
    vertical-align: middle;
}

tr:nth-child(even) {
    background: #f9f9f9;
}

tr:hover {
    background: #f2f7fb;
}

.column-cover {
    width: 4.5rem;
}

.column-title {
    width: 34%;
}

.literature-table-thumb {
    width: 2.75rem;
    height: 3.5rem;
    padding: 0.2rem;
    border: 1px solid var(--color-light-grey);
    background: #fff;
}

.literature-table-thumb img {
    object-fit: contain;
}

.literature-title {
    color: #01549e;
    font-size: var(--font-size-base);
    line-height: var(--line-height-tight);
}

.row-actions {
    color: var(--color-pip-gray);
}

.delete-row-action {
    display: inline;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: #a51600;
    cursor: pointer;
    font-family: inherit !important;
    font-size: inherit;
    line-height: inherit;
    text-transform: none;

    &:hover:not(:disabled) {
        background: transparent;
        color: #cd1b00;
        text-decoration: underline;
    }

    &:disabled {
        cursor: wait;
        opacity: 0.6;
    }
}

.pdf-name {
    max-width: 24rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.status-badge {
    display: inline-flex;
    padding: 0.12rem 0.45rem;
    border-radius: 999px;
    font-size: 0.65rem;
    font-weight: var(--font-weight-bold);
    letter-spacing: 0.04em;
    line-height: 1.4;
    text-transform: uppercase;
    white-space: nowrap;
}

.status-badge--published {
    color: #176527;
    background: #dff2e3;
}

.status-badge--draft {
    color: #5c4806;
    background: #fff2bd;
}

.status-badge--archived {
    color: #fff;
    background: var(--color-mine-shaft);
}

.status-badge--unset {
    color: var(--color-pip-gray);
    background: var(--color-light-grey);
}

.destination-state {
    padding: 0.1rem 0.35rem;
    border: 1px solid var(--color-light-grey);
    color: #999;
    background: #fff;
    font-size: 0.65rem;
    line-height: 1.4;
    white-space: nowrap;

    &.is-active {
        border-color: #8bbd95;
        color: #176527;
        background: #eaf5ec;
        font-weight: var(--font-weight-semibold);
    }
}
</style>
